// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { Tag } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// context
import { ContextProject } from '../../pages/Project/Project.context';

// mui
import {
	Autocomplete,
	AutocompleteProps,
	Box,
	Chip,
	FormControl,
	InputLabel,
	TextField,
} from '@mui/material';

// props
export type AutocompletePropsOmit = Omit<
	AutocompleteProps<string, boolean | undefined, boolean | undefined, boolean | undefined>,
	'defaultValue' | 'onChange' | 'options' | 'renderInput' | 'value'
>;

export interface AutocompleteTagsProps extends AutocompletePropsOmit {
	id?: string;
	label?: string;
	multiple?: boolean;
	name?: string;
	onChange?: (value: string[]) => void;
	placeholder?: string;
	required?: boolean;
	value?: string[];
}

export const AutocompleteTags = ({
	id,
	label,
	multiple = true,
	name = 'tags',
	onChange,
	placeholder,
	required,
	size = 'small',
	value = [],
	...props
}: AutocompleteTagsProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);

	// state
	const [tags, setTags] = useState<Tag[]>([]);

	// options
	const optionsTags = tags.map((tag) => tag.name);

	// fetch tags
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/tags?project=${project?.id}&fields=id,name&orderBy=name&order=ASC`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setTags(res.data);
			}
		},
	});

	const handleDelete = (e: MouseEvent<HTMLButtonElement>, valueToDelete: string) => {
		const valueNew = value.filter((entry) => entry !== valueToDelete);
		if (onChange) {
			onChange(valueNew);
		}
	};

	return (
		<>
			<FormControl sx={{ width: '100%' }}>
				<InputLabel
					htmlFor={id}
					required={required}
					shrink={false}
					sx={{ position: 'static', transform: 'none' }}>
					{label || t('tags')}
				</InputLabel>
				<Autocomplete
					freeSolo={true}
					id={id}
					multiple={multiple}
					onChange={(e, valueNew) => {
						const valueArray = valueNew as string[];
						if (onChange) {
							onChange(valueArray);
						}
					}}
					options={optionsTags}
					renderInput={(params) => (
						<TextField {...params} placeholder={placeholder || t('tags.placeholder')} />
					)}
					renderTags={(value) => {
						return value.map((item) => {
							return (
								<Chip
									key={item}
									label={item}
									onDelete={(e) => handleDelete(e, item)}
									sx={(theme) => {
										return {
											padding: theme.spacing(0, 0, 0, 1.5),
											margin: '2px',
										};
									}}
									variant="outlined"
								/>
							);
						});
					}}
					size={size}
					value={value}
					{...props}
				/>
			</FormControl>
			<Box hidden={true}>
				<TextField name={name} type="hidden" value={value.join(',')} />
			</Box>
		</>
	);
};
